import { createAsyncThunk, createSlice, createSelector } from '@reduxjs/toolkit';
import * as webViewerLicensesService from '../services/webViewerLicenses';
import { userConfigsSelectors } from './userConfigs';
import { entitiesAdapter } from './common/adapter';

const initialState = {
  isFetching: false,
  isCreating: false,
  isUpdating: false,
  isRemoving: false,
  isRestoring: false,

  fetchingError: null,
  creatingUpdatingError: null,
  removingRestoringError: null,
};

const sliceName = 'webViewerLicenses';

export const fetchAllWebViewerLicenses = createAsyncThunk(
  `${sliceName}/fetchAllWebViewerLicenses`,
  (args, thunkAPI) => {
    return webViewerLicensesService.getWebViewerLicenses().catch((error) => {
      error.clientMessage = "Can't get web viewer licenses from server";
      return thunkAPI.rejectWithValue(error);
    });
  }
);

export const fetchById = createAsyncThunk(
  `${sliceName}/fetchById`,
  (args, thunkAPI) => {
    return webViewerLicensesService.getWebViewerLicenseById(args.id)
      .catch((error) => {
        error.clientMessage = "Can't get web viewer license from server";
        return thunkAPI.rejectWithValue(error)
      });
  }
);

export const create = createAsyncThunk(
  `${sliceName}/create`,
  (args, thunkAPI) => {
    return webViewerLicensesService.addWebViewerLicense(args.serverId, args.featurePackages, args.count)
      .catch((error) => {
        error.clientMessage = "Can't post web viewer license to server";
        return thunkAPI.rejectWithValue(error)
      });
  }
);

export const update = createAsyncThunk(
  `${sliceName}/update`,
  (args, thunkAPI) => {
    return webViewerLicensesService.editWebViewerLicense(
      args.licenseId, args.featurePackages)
      .catch((error) => {
        error.clientMessage = "Can't update web viewer license on server";
        return thunkAPI.rejectWithValue(error)
      });
  }
);

export const restore = createAsyncThunk(
  `${sliceName}/restore`,
  (args, thunkAPI) => {
    return webViewerLicensesService
      .restoreWebViewerLicenses(args.webViewerLicenseIds)
      .catch((error) => {
        error.clientMessage = "Can't update web viewer licenses on server";
        return thunkAPI.rejectWithValue(error)
      });
  }
);

export const remove = createAsyncThunk(
  `${sliceName}/remove`,
  (args, thunkAPI) => {
    return webViewerLicensesService
      .removeWebViewerLicenses(args.webViewerLicenseIds)
      .catch((error) => {
        error.clientMessage = "Can't update web viewer licenses on server";
        return thunkAPI.rejectWithValue(error)
      });
  }
);

export const addToFavorites = createAsyncThunk(
  `${sliceName}/favoritesAdd`,
  (args) => {
    return webViewerLicensesService.addWebViewerLicenseToFavorites(args.id)
  }
);
export const removeFromFavorites = createAsyncThunk(
  `${sliceName}/favoritesRemove`,
  (args) => {
    return webViewerLicensesService.removeWebViewerLicenseFromFavorites(args.id)
  }
);

const webViewerLicenses = createSlice({
  name: sliceName,
  initialState: entitiesAdapter.getInitialState(initialState),
  reducers: {},
  extraReducers: {
    [fetchAllWebViewerLicenses.fulfilled]: (state, action) => {
      state.isFetching = false;
      entitiesAdapter.setAll(state, action);
    },
    [fetchAllWebViewerLicenses.pending]: (state) => {
      state.isFetching = true;
      state.fetchingError = null;
    },
    [fetchAllWebViewerLicenses.rejected]: (state, action) => {
      state.isFetching = false;
      state.fetchingError = action.payload;
    },

    [fetchById.fulfilled]: (state, action) => {
      state.isFetching = false;
      entitiesAdapter.upsertOne(state, action);
    },
    [fetchById.pending]: (state) => {
      state.isFetching = true;
      state.fetchingError = null;
    },
    [fetchById.rejected]: (state, action) => {
      state.isFetching = false;
      state.fetchingError = action.payload;
    },

    [create.fulfilled]: (state, action) => {
      state.isCreating = false;
      entitiesAdapter.upsertMany(state, action);
    },
    [create.pending]: (state) => {
      state.isCreating = true;
      state.fetchingError = null;
      state.creatingUpdatingError = null;
    },
    [create.rejected]: (state, action) => {
      state.isCreating = false;
      state.creatingUpdatingError = action.payload;
    },

    [update.fulfilled]: (state, action) => {
      state.isUpdating = false;
      entitiesAdapter.upsertOne(state, action);
    },
    [update.pending]: (state) => {
      state.isUpdating = true;
      state.creatingUpdatingError = null;
    },
    [update.rejected]: (state, action) => {
      state.isUpdating = false;
      state.creatingUpdatingError = action.payload;
    },

    [remove.fulfilled]: (state, action) => {
      state.isRemoving = false;
      entitiesAdapter.upsertMany(state, action);
    },
    [remove.pending]: (state) => {
      state.isRemoving = true;
      state.removingRestoringError = null;
    },
    [remove.rejected]: (state, action) => {
      state.isRemoving = false;
      state.removingRestoringError = action.payload;
    },

    [restore.fulfilled]: (state, action) => {
      state.isRestoring = false;
      entitiesAdapter.upsertMany(state, action);
    },
    [restore.pending]: (state) => {
      state.isRestoring = true;
      state.removingRestoringError = null;
    },
    [restore.rejected]: (state, action) => {
      state.isRestoring = false;
      state.removingRestoringError = action.payload;
    },

    [addToFavorites.fulfilled]: entitiesAdapter.upsertOne,
    [removeFromFavorites.fulfilled]: entitiesAdapter.upsertOne,
  },
});

const { reducer } = webViewerLicenses;

const webViewerLicensesAdapterSelectors = entitiesAdapter.getSelectors(
  (state) => state[sliceName]
);

const selectedWebViewerLicensesSelector = createSelector(
  (state) => userConfigsSelectors.selectedItems(state, sliceName),
  (state) => webViewerLicensesAdapterSelectors.selectEntities(state),
  (selectedIds, entities) => selectedIds.map((id) => entities[id]),
)

export const webViewerLicensesSelectors = {
  ...webViewerLicensesAdapterSelectors,
  isFetching: (state) => state[sliceName].isFetching,
  fetchingError: (state) => state[sliceName].fetchingError,

  state: (state) => state[sliceName],
  selectedWebViewerLicenses: selectedWebViewerLicensesSelector,
};

export default reducer;


// WEBPACK FOOTER //
// ./src/store/webViewerLicensesSlice.js